import { ApplicationRef, ComponentFactoryResolver, inject, Injectable, Injector, Type } from "@angular/core";

import { Closable } from "./closable";
import { ModalRef } from "./modal-ref";
import { OverlayRef } from "./overlay-ref";
import { closeSymbol, instanceSymbol } from "./symbols";

interface Options {
	backdrop?: boolean;
	width?: number | string;
	height?: number | string;
	disableClose?: boolean;
	showCloseButton?: boolean;
}

const defaultOptions: Options = {
	backdrop: true,
	height: "600px",
	width: "800px",
};

/** @deprecated Use the MatDialog from angular-material instead. */
@Injectable()
export class ModalService implements Closable {
	private componentFactoryResolver = inject(ComponentFactoryResolver);
	private injector = inject(Injector);
	private appRef = inject(ApplicationRef);

	private static id = 1;

	private container: HTMLElement;
	private componentRef;

	open<T, R = unknown>(component: Type<T>, customOptions: Options = {}) {
		const options = {
			...defaultOptions,
			...customOptions,
		};

		this.hideScrollbar();

		const overlayRef = new OverlayRef();
		const modalRef = new ModalRef<T, R>(overlayRef, this);

		const overlay = this.getOverlay();
		const container = this.createModalView(overlayRef, modalRef, overlay, options);

		const factory = this.componentFactoryResolver.resolveComponentFactory(component);

		const injector = Injector.create({
			providers: [{ provide: ModalRef, useValue: modalRef }],
			parent: this.injector,
		});

		this.componentRef = factory.create(injector);

		modalRef[instanceSymbol] = this.componentRef.instance;

		this.appRef.attachView(this.componentRef.hostView);

		const componentHTML = this._getComponentRootNode(this.componentRef);
		componentHTML.classList.add("pv-modal-content");

		container.appendChild(componentHTML);

		return modalRef;
	}

	[closeSymbol]() {
		if (this.container) {
			document.body.removeChild(this.container);
			this.container = undefined;
		}

		this.showScrollBar();
		this.componentRef.destroy();
	}

	private _getComponentRootNode(componentRef): HTMLElement {
		return componentRef.hostView.rootNodes[0] as HTMLElement;
	}

	private createModalView(overlayRef: OverlayRef, modalRef: ModalRef, parent: HTMLElement, options: Options) {
		const container = document.createElement("div");

		container.classList.add("pv-modal-overlay__container");
		container.id = `pv-modal-overlay__container-${ModalService.id++}`;
		container.style.position = "relative";
		container.style.height = typeof options.height === "string" ? options.height : `${options.height || "600"}px`;
		container.style.width = typeof options.width === "string" ? options.width : `${options.width || "800"}px`;

		if (options.backdrop) {
			const backdrop = document.createElement("div");
			backdrop.classList.add("pv-modal-overlay__backdrop");

			if (!options.disableClose) {
				const onBackdropClick = () => {
					overlayRef.onBackdropClick();
					modalRef.close();

					backdrop.removeEventListener("click", onBackdropClick);
				};

				backdrop.addEventListener("click", onBackdropClick);
			}

			parent.appendChild(backdrop);
		}

		if (options.showCloseButton) {
			const closeButton = document.createElement("i");

			closeButton.classList.add("fa", "fa-times");

			closeButton.style.position = "absolute";
			closeButton.style.right = "0px";
			closeButton.style.margin = "10px";
			closeButton.style.zIndex = "1";
			closeButton.style.color = "#3c5d6e";
			closeButton.style.fontSize = "22px";
			closeButton.style.cursor = "pointer";

			container.appendChild(closeButton);

			const onCancelClick = () => {
				modalRef.close();

				closeButton.removeEventListener("click", onCancelClick);
			};

			closeButton.addEventListener("click", onCancelClick);
		}

		if (options.showCloseButton || (options.backdrop && !options.disableClose)) {
			const onEscapeKeyPress = (event) => {
				const isEscape = event.key === "Escape" || event.key === "Esc" || event.keyCode === 27;

				if (!isEscape) {
					return;
				}

				modalRef.close();

				document.removeEventListener("keyup", onEscapeKeyPress);
			};

			document.addEventListener("keyup", onEscapeKeyPress);
		}

		parent.appendChild(container);

		return container;
	}

	private getOverlay() {
		if (!this.container) {
			this.container = this.createOverlay();
		}

		return this.container;
	}

	private createOverlay() {
		const overlay = document.createElement("div");
		overlay.classList.add("pv-modal-overlay");

		document.body.appendChild(overlay);

		return overlay;
	}

	private hideScrollbar() {
		const body = document.querySelector("body");
		body.style.overflow = "hidden";
	}

	private showScrollBar() {
		const body = document.querySelector("body");
		body.style.overflow = "auto";
	}
}
